import React, { useRef, useState, useCallback, useEffect, Suspense, lazy } from "react"
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material"
import SurfboardSvg from "./SurfboardSvg"

const BoardViewer3DEmbed = lazy(() =>
  import("../../BoardViewer/BoardViewer3D").then(m => ({ default: m.BoardViewer3DEmbed }))
)

const CustomOrderSurfboards = ({
  selectedLogoOption,
  selectedDeckPatchOption,
  selectedDeckColorOption,
  selectedBottomColorOption,
  selectedTailPatchOption,
  selectedFinBoxColorOption,
  selectedFinSetupOption,
  selectedNoseShapeOption,
  selectedTailShapeOption,
  selectedPinLineColorOption,
  selectedLengthOption,
  selectedWidthOption,
  selectedStringerTypeOption,
  selectedNumberOfStringersOption,
  selectedGlossFinishOption,
}) => {
  const surfboardDeckSvgRef = useRef(null)
  const surfboardBottomSvgRef = useRef(null)
  const mobileAnchorRef = useRef(null)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  const [view3D, setView3D] = useState(false)
  const [scrollY, setScrollY] = useState(0)
  const [mobileExpanded, setMobileExpanded] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  // Watch when the inline anchor scrolls into view on mobile
  useEffect(() => {
    if (isDesktop || !mobileAnchorRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => setMobileExpanded(entry.isIntersecting),
      { threshold: 0.15 }
    )
    observer.observe(mobileAnchorRef.current)
    return () => observer.disconnect()
  }, [isDesktop])
  const fixedTop = Math.max(40, 280 - scrollY * 0.8)
  const glowRef = useRef(null)
  const handleRotation = useCallback((rot, polar) => {
    if (glowRef.current) {
      const sx = 0.5 + Math.abs(Math.sin(rot)) * 0.5
      const sy = 0.3 + Math.abs(Math.sin(polar)) * 0.7
      glowRef.current.style.transform = `translate(-50%, -50%) scaleX(${sx}) scaleY(${sy})`
    }
  }, [])

  const deckColor = selectedDeckColorOption?.value || "#F5F5F5"
  const bottomColor = selectedBottomColorOption?.value || "#F5F5F5"
  const stringerColor3D = selectedStringerTypeOption?.extra_data?.hex_color || "#fff9c7"
  const finColor3D = selectedFinBoxColorOption?.value || "#000000"
  const logoType3D = (selectedLogoOption?.value || "Diamond").toLowerCase()
  const pinLineColor3D = selectedPinLineColorOption?.value || null
  const lengthInches = selectedLengthOption?.extra_data?.total_inches || null
  const widthInches = selectedWidthOption?.extra_data?.total_inches || null

  const panelContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
      }}
    >
      {/* 2D / 3D Toggle — compact, top-right feel */}
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {["2D", "3D"].map((label) => (
          <Box
            key={label}
            onClick={() => setView3D(label === "3D")}
            sx={{
              px: 1.2, py: 0.2, borderRadius: 0.8, cursor: "pointer",
              fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.08em", fontFamily: "'Josefin Sans', sans-serif",
              color: (label === "3D") === view3D ? "#fff" : "#c8b99a",
              bgcolor: (label === "3D") === view3D ? "rgba(255,255,255,0.12)" : "transparent",
              border: (label === "3D") === view3D ? "1px solid rgba(255,255,255,0.25)" : "1px solid transparent",
              transition: "all 0.15s",
              "&:hover": { color: "#fff" },
            }}
          >{label}</Box>
        ))}
      </Box>

      {view3D ? (
        /* 3D Viewer */
        <Box sx={{
          width: "100%", height: "calc(100vh - 100px)", pb: 4,
          position: "relative",
        }}>
          <Box
            ref={glowRef}
            sx={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%) scaleX(1)",
              width: "75%", height: "65%",
              borderRadius: "50%",
              background: `radial-gradient(ellipse, ${deckColor === "#F5F5F5" ? bottomColor : deckColor}66 0%, transparent 70%)`,
              filter: "blur(50px)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
          <Suspense fallback={<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#c8b99a", fontFamily: "'Josefin Sans', sans-serif", fontSize: "0.8rem" }}>Loading 3D viewer...</Box>}>
            <BoardViewer3DEmbed
              deckColor={deckColor}
              bottomColor={bottomColor}
              stringerColor={stringerColor3D}
              finColor={finColor3D}
              isGloss={!!selectedGlossFinishOption}
              logoType={logoType3D}
              onRotation={handleRotation}
              noseShape={selectedNoseShapeOption?.value || "Round"}
              tailShape={selectedTailShapeOption?.value || "Square"}
              lengthInches={lengthInches}
              widthInches={widthInches}
              pinLineColor={pinLineColor3D}
              deckPatch={selectedDeckPatchOption?.value || "None"}
              tailPatch={selectedTailPatchOption?.value || "None"}
              finSetup={selectedFinSetupOption?.value || "Thruster"}
              stringerCount={{ None: 0, Single: 1, Double: 2, Triple: 3, Five: 5 }[selectedNumberOfStringersOption?.value] ?? 1}
            />
          </Suspense>
        </Box>
      ) : (
      <>
      {/* Labels */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: { xs: 480, lg: 560, xl: 660 },
          gap: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant="overline"
          sx={{
            flex: 1,
            textAlign: "center",
            color: "#c8b99a",
            letterSpacing: "0.25em",
            fontSize: "0.65rem",
            fontFamily: "'Josefin Sans', sans-serif",
          }}
        >
          Deck
        </Typography>
        <Typography
          variant="overline"
          sx={{
            flex: 1,
            textAlign: "center",
            color: "#c8b99a",
            letterSpacing: "0.25em",
            fontSize: "0.65rem",
            fontFamily: "'Josefin Sans', sans-serif",
          }}
        >
          Bottom
        </Typography>
      </Box>

      {/* SVG container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 2, md: 3 },
          maxWidth: { xs: 480, lg: 560, xl: 660 },
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <SurfboardSvg
            svgRef={surfboardDeckSvgRef}
            style={{ width: "100%", height: "75vh", maxHeight: "700px" }}
            selectedLogoOption={selectedLogoOption}
            selectedDeckPatchOption={selectedDeckPatchOption}
            selectedDeckColorOption={selectedDeckColorOption}
            selectedBottomColorOption={selectedBottomColorOption}
            selectedTailPatchOption={selectedTailPatchOption}
            selectedFinBoxColorOption={selectedFinBoxColorOption}
            selectedFinSetupOption={selectedFinSetupOption}
            selectedNoseShapeOption={selectedNoseShapeOption}
            selectedTailShapeOption={selectedTailShapeOption}
            selectedPinLineColorOption={selectedPinLineColorOption}
            selectedLengthOption={selectedLengthOption}
            selectedWidthOption={selectedWidthOption}
            selectedStringerTypeOption={selectedStringerTypeOption}
            selectedNumberOfStringersOption={selectedNumberOfStringersOption}
            deck={true}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <SurfboardSvg
            svgRef={surfboardBottomSvgRef}
            style={{ width: "100%", height: "75vh", maxHeight: "700px" }}
            selectedLogoOption={selectedLogoOption}
            selectedDeckPatchOption={selectedDeckPatchOption}
            selectedDeckColorOption={selectedDeckColorOption}
            selectedBottomColorOption={selectedBottomColorOption}
            selectedTailPatchOption={selectedTailPatchOption}
            selectedFinBoxColorOption={selectedFinBoxColorOption}
            selectedFinSetupOption={selectedFinSetupOption}
            selectedNoseShapeOption={selectedNoseShapeOption}
            selectedTailShapeOption={selectedTailShapeOption}
            selectedPinLineColorOption={selectedPinLineColorOption}
            selectedLengthOption={selectedLengthOption}
            selectedWidthOption={selectedWidthOption}
            selectedStringerTypeOption={selectedStringerTypeOption}
            selectedNumberOfStringersOption={selectedNumberOfStringersOption}
          />
        </Box>
      </Box>

      {/* Disclaimer */}
      <Typography
        variant="caption"
        sx={{
          color: "#7ea8be",
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: "0.7rem",
          letterSpacing: "0.05em",
          textAlign: "center",
        }}
      >
        Finbox placement and outline not completely accurate
      </Typography>
      </>
      )}
    </Box>
  )

  if (isDesktop) {
    return (
      <>
        {/* Spacer to hold grid space */}
        <Grid item md={7} sx={{ visibility: "hidden" }}>
          {panelContent}
        </Grid>
        {/* Fixed panel that follows the viewport */}
        <Box
          sx={{
            position: "fixed",
            top: `${fixedTop}px`,
            left: 0,
            height: `calc(100vh - ${fixedTop}px)`,
            width: "58.333%", /* 7/12 of container */
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1,
            px: 3,
            pt: 0,
          }}
        >
          <Box sx={{ pointerEvents: "auto", width: "100%", height: "100%", mt: -2 }}>
            {panelContent}
          </Box>
        </Box>
      </>
    )
  }

  // Mobile: floating thumbnail top-right, expands to full when anchor is in view
  return (
    <>
      {/* Floating spinning 3D preview — hidden once expanded */}
      {!mobileExpanded && (
        <Box
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
          sx={{
            position: "fixed",
            top: Math.max(12, 220 - scrollY),
            right: 8,
            width: 120,
            height: 160,
            zIndex: 10,
            cursor: "pointer",
            transition: "opacity 0.3s",
            pointerEvents: "auto",
          }}
        >
          <Suspense fallback={null}>
            <BoardViewer3DEmbed
              deckColor={deckColor}
              bottomColor={bottomColor}
              stringerColor={stringerColor3D}
              finColor={finColor3D}
              isGloss={!!selectedGlossFinishOption}
              logoType={logoType3D}
              noseShape={selectedNoseShapeOption?.value || "Round"}
              tailShape={selectedTailShapeOption?.value || "Square"}
              lengthInches={lengthInches}
              widthInches={widthInches}
              pinLineColor={pinLineColor3D}
              deckPatch={selectedDeckPatchOption?.value || "None"}
              tailPatch={selectedTailPatchOption?.value || "None"}
              finSetup={selectedFinSetupOption?.value || "Thruster"}
              stringerCount={{ None: 0, Single: 1, Double: 2, Triple: 3, Five: 5 }[selectedNumberOfStringersOption?.value] ?? 1}
            />
          </Suspense>
        </Box>
      )}
      {/* Inline anchor — full panel when scrolled into view */}
      <Grid item xs={12} ref={mobileAnchorRef}>
        {panelContent}
      </Grid>
    </>
  )
}

export default CustomOrderSurfboards
